import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import React, { useEffect, useId } from 'react';

interface SlideInPanelProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  modal?: boolean;
  title?: string;
  width?: string;
}

export const SlideInPanel = ({
  open,
  onClose,
  children,
  modal = true,
  title,
  width = 'max-w-md',
}: SlideInPanelProps) => {
  const titleId = useId();

  useEffect(() => {
    if (modal || !open) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (
        event.key !== 'Escape' ||
        event.defaultPrevented ||
        document.querySelector('[role="dialog"][aria-modal="true"]')
      ) {
        return;
      }
      event.preventDefault();
      onClose();
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [modal, onClose, open]);

  const contents = (
    <div className="flex h-full flex-col bg-white text-left shadow-xl">
      <div className="flex items-center justify-between border-b border-grey-900 px-5 py-4">
        {title ? (
          <h2 className="text-lg font-semibold text-grey-100" id={titleId}>
            {title}
          </h2>
        ) : null}
        <button
          className="shrink-0 rounded-full p-1.5 text-grey-400 transition-colors hover:bg-grey-900 hover:text-grey-100 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary-50/30"
          onClick={onClose}
          type="button"
        >
          <span className="sr-only">Close panel</span>
          <XMarkIcon aria-hidden="true" className="size-5" />
        </button>
      </div>
      <div className="relative flex-1 overflow-y-auto px-5 py-5">{children}</div>
    </div>
  );

  if (!modal) {
    if (!open) return null;
    return (
      <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden">
        <div className="absolute inset-y-0 right-0 flex max-w-full">
          <section
            aria-label={title ? undefined : 'Panel'}
            aria-labelledby={title ? titleId : undefined}
            aria-modal="false"
            className={classNames('pointer-events-auto w-screen', width)}
            role="dialog"
          >
            {contents}
          </section>
        </div>
      </div>
    );
  }

  return (
    <Dialog
      aria-label={title ? undefined : 'Panel'}
      aria-labelledby={title ? titleId : undefined}
      className="relative z-50"
      onClose={onClose}
      open={open}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-grey-100/60 backdrop-blur-sm transition-opacity duration-300 ease-in-out data-closed:opacity-0"
      />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
            <DialogPanel
              transition
              className={classNames(
                'pointer-events-auto w-screen transform transition duration-300 ease-in-out',
                'data-closed:translate-x-full',
                width
              )}
            >
              {contents}
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
